import { useEffect, useState } from "react";
import { Plus, Minus, Rocket, Layers } from "lucide-react";

const BLUE = "#00A3FF";
const BLUE_HI = "#4CC3FF";
const BLUE_GLOW = "rgba(0,163,255,0.35)";
const GOLD = "#F5B800";
const SILVER = "#C7CBD1";
const SILVER_HI = "#EDEDF0";
const DIM = "rgba(199,203,209,0.7)";
const PANEL = "#0F0F13";

const TABS = [
  {
    key: "launch",
    icon: Rocket,
    label: "Website Launch",
    priceLabel: "starting at $299.99",
    qas: [
      { q: "What is the $299.99 Website Launch?", a: "A production-ready launch site — hero, services, contact, mobile-responsive, AI chat, hosting-ready, and Google-indexed — delivered flat for $299.99. No monthly SEO retainer, no drip pricing." },
      { q: "How fast can you launch it?", a: "Most Launch sites go live in 5–10 business days from the moment we have your brand assets and content sign-off. Rush turnarounds available." },
      { q: "Do I own the site and domain?", a: "Yes — 100%. You own the domain, the codebase, the content, and the CRM data. No hostage-fee model. If you leave, everything walks with you." },
      { q: "Does the $299.99 include SEO?", a: "Yes. Every Launch ships with schema markup, XML sitemap, optimized meta tags, Core Web Vitals tuning, and Google Business Profile alignment. SEO is baked in, not billed separately." },
      { q: "Do you charge monthly?", a: "No mandatory monthly fees. You only pay hosting (~$15–25/mo depending on tier). Optional support retainers exist but are opt-in, never required." },
      { q: "What if I need changes after launch?", a: "First 30 days of content edits and bug fixes are free. After that, small changes are $50–150 per request or included in an optional support plan." },
      { q: "How many pages does a Launch site have?", a: "5–12 pages typical — home, about, services, contact, plus service and city pages tailored to your business. Need more? Upgrade to Custom Studio for 700–1,500 page authority builds." },
      { q: "Can I upgrade to Custom Studio later?", a: "Absolutely. Everything from your $299.99 Launch site is portable and re-usable. You only pay the delta up to the Custom tier — never start from zero." },
    ],
  },
  {
    key: "custom",
    icon: Layers,
    label: "Custom Studio",
    priceLabel: "starting at $4,000",
    qas: [
      { q: "What makes PlugitAI Custom different?", a: "We combine enterprise-level strategy, custom design, advanced SEO and intelligent automation — personally led from start to finish. You work directly with the owner, not account managers, so your vision, systems and growth are always aligned." },
      { q: "What does a custom website cost?", a: "Custom Studio builds start at $4,000 and scale up to $12,000 depending on page count, complexity, integrations, and automation depth. Payment plans available." },
      { q: "Do you offer affordable Website Launches too?", a: "Yes — $299.99 flat for a Launch tier. Perfect for owners who want to test-and-scale before committing to a full Custom build." },
      { q: "Does every custom website include SEO?", a: "Every Custom Studio project ships with a full technical SEO audit, schema markup, XML sitemap, page-speed optimization, local SEO, and 90 days of ranking monitoring included." },
      { q: "Can PlugitAI build 700–1,500 pages?", a: "Yes. Programmatic SEO builds with 700–1,500 pages (city × service combinations, individual product/pest/service pages, blog libraries) are our specialty. See our Miss Rodriguez case study for a live example." },
      { q: "Can you connect our existing business systems?", a: "Yes — we integrate with your CRM, phone system, calendar, invoicing, SMS/email, review platforms, POS, and inventory tools. Every custom build includes 3 integrations; more available in the roadmap." },
      { q: "Who owns the website after completion?", a: "You do — 100%. Domain, codebase, content, database, and AI agent configuration are all yours. We provide handoff documentation and a 30-day transition window." },
      { q: "How long does a Custom Studio build take?", a: "3–8 weeks depending on scope. Milestone payments align with delivery: 30% at kickoff, 40% at design approval, 30% at launch. No surprises, no scope creep without written change orders." },
    ],
  },
];

export default function QAndA() {
  const [activeTab, setActiveTab] = useState("launch");
  const [openKey, setOpenKey] = useState(`${TABS[0].key}-0`);

  const tab = TABS.find((t) => t.key === activeTab);

  // Inject FAQPage JSON-LD for both tabs so Google can surface rich answers
  useEffect(() => {
    const id = "ld-qanda-faq";
    const allQAs = TABS.flatMap((t) => t.qas);
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allQAs.map((qa) => ({
        "@type": "Question",
        name: qa.q,
        acceptedAnswer: { "@type": "Answer", text: qa.a },
      })),
    };
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { const x = document.getElementById(id); if (x) x.remove(); };
  }, []);

  return (
    <section className="relative z-10 py-16 md:py-24" data-testid="qanda-section">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        {/* Header + tabs */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.32em] mb-3" style={{ color: BLUE_HI }}>
              Answers · both tiers
            </div>
            <h2 className="font-black tracking-[-0.015em] leading-[1.02]"
                style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.6rem)", color: SILVER_HI }}
                data-testid="qanda-heading">
              Questions Before We Build Something Great?
            </h2>
          </div>
          {/* Tabs */}
          <div className="inline-flex flex-wrap items-center gap-2 rounded-lg p-1.5 border shrink-0"
               style={{ background: PANEL, borderColor: "rgba(199,203,209,0.14)" }}>
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = t.key === activeTab;
              return (
                <button key={t.key}
                        type="button"
                        onClick={() => { setActiveTab(t.key); setOpenKey(`${t.key}-0`); }}
                        className="inline-flex items-center gap-2 px-3 md:px-4 h-10 rounded-md text-[11px] md:text-[12px] font-black uppercase tracking-[0.16em]"
                        style={{
                          background: active ? "rgba(0,163,255,0.14)" : "transparent",
                          color: active ? SILVER_HI : DIM,
                          border: active ? `1px solid ${BLUE}66` : "1px solid transparent",
                          boxShadow: active ? `inset 0 0 20px ${BLUE_GLOW}` : "none",
                        }}
                        data-testid={`qanda-tab-${t.key}`}>
                  <Icon size={13} style={{ color: active ? BLUE_HI : DIM }} />
                  <span>{t.label}</span>
                  <span className="hidden sm:inline text-[10px] font-normal opacity-70 normal-case">— {t.priceLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Q&A two-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {tab.qas.map((qa, i) => {
            const key = `${tab.key}-${i}`;
            const open = openKey === key;
            return (
              <div key={key}
                   className="rounded-lg border overflow-hidden transition-all"
                   style={{
                     background: PANEL,
                     borderColor: open ? `${BLUE}55` : "rgba(199,203,209,0.10)",
                     boxShadow: open ? `inset 0 0 40px -20px ${BLUE_GLOW}` : "none",
                   }}>
                <button type="button"
                        onClick={() => setOpenKey(open ? null : key)}
                        className="w-full flex items-start gap-4 text-left px-4 md:px-5 py-4"
                        data-testid={`qanda-q-${tab.key}-${i}`}>
                  <span className="shrink-0 font-black text-sm md:text-base pt-0.5"
                        style={{ color: open ? BLUE_HI : DIM, fontVariantNumeric: "tabular-nums" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 font-bold text-sm md:text-base leading-snug"
                        style={{ color: open ? SILVER_HI : SILVER }}>
                    {qa.q}
                  </span>
                  <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center border"
                        style={{
                          background: open ? BLUE : "transparent",
                          borderColor: open ? BLUE : "rgba(199,203,209,0.25)",
                          color: open ? "#0A0A0C" : SILVER,
                          transition: "all 200ms",
                        }}>
                    {open ? <Minus size={13} /> : <Plus size={13} />}
                  </span>
                </button>
                {open && (
                  <div className="px-4 md:px-5 pb-5 pt-1 pl-[52px] md:pl-[64px] text-sm leading-relaxed animate-fadeUp"
                       style={{ color: DIM }}
                       data-testid={`qanda-a-${tab.key}-${i}`}>
                    {qa.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA strip — the Ready to Build banner from the reference */}
        <div className="mt-10 rounded-xl border overflow-hidden relative"
             style={{
               background: `linear-gradient(135deg, rgba(0,163,255,0.14) 0%, ${PANEL} 60%, ${PANEL} 100%)`,
               borderColor: `${BLUE}44`,
               boxShadow: `0 30px 80px -30px ${BLUE_GLOW}`,
             }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-6 md:px-8 py-6 md:py-7">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-1" style={{ color: BLUE_HI }}>
                Ready when you are
              </div>
              <div className="font-black leading-tight" style={{ fontSize: "clamp(1.3rem, 2.6vw, 1.9rem)", color: SILVER_HI }}>
                Ready to Build Beyond Ordinary?
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a href="tel:+12148155966"
                 className="inline-flex items-center gap-2 h-11 px-5 rounded-md text-[11px] font-black uppercase tracking-[0.18em]"
                 style={{
                   background: `linear-gradient(135deg, ${BLUE_HI} 0%, ${BLUE} 100%)`,
                   color: "#0A0A0C",
                   boxShadow: `0 14px 30px -12px ${BLUE_GLOW}`,
                 }}
                 data-testid="qanda-cta-custom">
                Start a Custom project →
              </a>
              <a href="tel:+12148155966"
                 className="inline-flex items-center gap-2 h-11 px-5 rounded-md text-[11px] font-black uppercase tracking-[0.18em] border"
                 style={{ borderColor: `${GOLD}77`, color: GOLD }}
                 data-testid="qanda-cta-launch">
                Explore Website Launch →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
